// Global Instructions Rule Applied!
//
// Investor statement runs (spec A3.14 stage 5): the runs grid, one run with
// its funds and stored statements, and the actions on it - create, add and
// remove funds, generate, mark as issued, delete - and "out of date" and the
// runs a fund is in. The stored PDFs themselves download through the generic
// files service. Sending (stage 6): what "Send statements" would create, the
// draft statement email, and "Refresh from statement run".

import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import { filenameFromContentDisposition } from '../../../core/utils/contentDisposition.ts'
import { saveBlob } from '../../document_reports/services/documentReportsService.ts'
import type { RouteDefinition } from '../../../core/utils/endpoints.ts'
import {
  InvestorStatementRunRecord,
  validateInvestorStatementRunRecord
} from '../../../state_manager/data_models/investor_statement_run_record.ts'
import {
  FundStatementRuns,
  InvestorStatementRun,
  InvestorStatementRunFundOptions,
  InvestorStatementRunSavePayload,
  RunDescriptionWarnings,
  RunFundChanges,
  validateFundStatementRuns,
  validateInvestorStatementRun,
  validateInvestorStatementRunFundOptions,
  validateRunDescriptionWarnings,
  validateRunFundChanges
} from '../../../state_manager/data_models/investor_statement_run.ts'
import {
  InvestorStatementSendPlan,
  validateInvestorStatementSendPlan
} from '../../../state_manager/data_models/investor_statement_send_plan.ts'
import { FilterValue, SortConfig } from '../../global_configs/tableViewPreferences.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

export interface InvestorStatementRunsPaginatedResponse {
  data: InvestorStatementRunRecord[]
  hasMore: boolean
  totalCount: number
  error?: string
}

export interface InvestorStatementRunResponse {
  data: InvestorStatementRun | null
  error?: string
  notFound?: boolean
}

function authHeaders(accessToken: string | null): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

/** The server's own sentence for a failed request, whichever key carries it. */
async function failureText(response: Response): Promise<string> {
  if (response.status === 401) return 'Authentication failed - please log in again'
  const body = (await response.json().catch(() => ({}))) as { error?: string; message?: string }
  return body.error || body.message || `Request failed: ${response.status}`
}

const unexpected = (error: unknown): string =>
  error instanceof Error ? error.message : 'An unexpected error occurred'

export const fetchInvestorStatementRunsFiltered = async (
  sorting: SortConfig[] = [],
  filters: FilterValue[] = [],
  page: number = 1,
  pageSize: number | null = null,
  accessToken: string | null = null
): Promise<InvestorStatementRunsPaginatedResponse> => {
  try {
    const requestBody = {
      data: {
        page,
        page_size: pageSize ?? 200,
        sorting: sorting.map((s) => ({ key: s.key, direction: s.direction })),
        filters: filters.map((f) => ({ key: f.key, value: f.value }))
      }
    }
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INVESTOR_STATEMENTS.FILTER), {
      method: API_ROUTES.INVESTOR_STATEMENTS.FILTER.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify(requestBody)
    })
    if (!response.ok) throw new Error(await failureText(response))
    const json = (await response.json()) as { data?: unknown[]; has_more?: boolean; count?: number }
    return {
      data: validateInvestorStatementRunRecord(Array.isArray(json.data) ? json.data : [], true),
      hasMore: Boolean(json.has_more),
      totalCount: Number(json.count ?? 0)
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching statement runs:', error)
    return { data: [], hasMore: false, totalCount: 0, error: unexpected(error) }
  }
}

export const fetchInvestorStatementRunsFilterOptions = async (
  accessToken: string | null = null
): Promise<{ data: Record<string, Array<{ value: string; label: string }>>; error?: string }> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INVESTOR_STATEMENTS.FILTER_OPTIONS), {
      method: API_ROUTES.INVESTOR_STATEMENTS.FILTER_OPTIONS.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) throw new Error(await failureText(response))
    const json = (await response.json()) as { data?: Record<string, Array<{ value: string; label: string }>> }
    return { data: json.data ?? {} }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching statement run filter options:', error)
    return { data: {}, error: unexpected(error) }
  }
}

/** One run: its details, its funds with where each stands, its statements. */
export const fetchInvestorStatementRun = async (
  runId: string,
  accessToken: string | null = null
): Promise<InvestorStatementRunResponse> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INVESTOR_STATEMENTS.RUN, { id: runId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.INVESTOR_STATEMENTS.RUN.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) {
      return { data: null, error: await failureText(response), notFound: response.status === 404 }
    }
    const json = (await response.json()) as { data?: unknown }
    if (!json.data) return { data: null, error: 'Statement run not found', notFound: true }
    return { data: validateInvestorStatementRun(json.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching statement run:', error)
    return { data: null, error: unexpected(error) }
  }
}

/** Create a run. A refusal (no name, a period end that is not a quarter end)
 *  comes back as `error`, the server's own sentence. */
export const createInvestorStatementRun = async (
  payload: InvestorStatementRunSavePayload,
  accessToken: string | null = null
): Promise<InvestorStatementRunResponse> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INVESTOR_STATEMENTS.RUN_SAVE), {
      method: API_ROUTES.INVESTOR_STATEMENTS.RUN_SAVE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: payload })
    })
    if (!response.ok) return { data: null, error: await failureText(response) }
    const json = (await response.json()) as { data?: unknown }
    return { data: validateInvestorStatementRun(json.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error creating statement run:', error)
    return { data: null, error: unexpected(error) }
  }
}

/** The funds and consolidated funds that can join a run. */
export const fetchRunFundOptions = async (
  runId: string,
  accessToken: string | null = null
): Promise<{ data: InvestorStatementRunFundOptions | null; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INVESTOR_STATEMENTS.RUN_FUND_OPTIONS, { id: runId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.INVESTOR_STATEMENTS.RUN_FUND_OPTIONS.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) return { data: null, error: await failureText(response) }
    const json = (await response.json()) as { data?: unknown }
    return { data: validateInvestorStatementRunFundOptions(json.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching run fund options:', error)
    return { data: null, error: unexpected(error) }
  }
}

/** Funds and consolidated funds join a run, all or nothing. */
export const addRunFunds = async (
  runId: string,
  fundIds: string[],
  fundGroupIds: string[],
  accessToken: string | null = null
): Promise<InvestorStatementRunResponse> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INVESTOR_STATEMENTS.RUN_ADD_FUNDS, { id: runId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.INVESTOR_STATEMENTS.RUN_ADD_FUNDS.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: { fund_ids: fundIds, fund_group_ids: fundGroupIds } })
    })
    if (!response.ok) return { data: null, error: await failureText(response) }
    const json = (await response.json()) as { data?: unknown }
    return { data: validateInvestorStatementRun(json.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error adding funds to a statement run:', error)
    return { data: null, error: unexpected(error) }
  }
}

/** A fund leaves its run; its stored statements go to the trash. */
export const removeRunFund = async (
  runFundId: string,
  accessToken: string | null = null
): Promise<{ success: boolean; filesNotRemoved: number; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INVESTOR_STATEMENTS.RUN_REMOVE_FUND, { id: runFundId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.INVESTOR_STATEMENTS.RUN_REMOVE_FUND.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) return { success: false, filesNotRemoved: 0, error: await failureText(response) }
    const json = (await response.json().catch(() => ({}))) as { data?: { files_not_removed?: number } }
    return { success: true, filesNotRemoved: json.data?.files_not_removed ?? 0 }
  } catch (error) {
    return { success: false, filesNotRemoved: 0, error: unexpected(error) }
  }
}

/** What a generation is asked for: every fund of the run, the given funds,
 *  the given statements of those funds, or the given investor performance
 *  decks (run-level documents, made again on their own); and the document
 *  groups (reports, statements), every group when absent. */
export interface GenerateTarget {
  runFundIds?: string[] | null
  statementIds?: string[] | null
  documentGroupCodes?: string[] | null
  accountDocumentIds?: string[] | null
}

/**
 * Generate (or generate again) the statements of a run: every fund of it,
 * the given funds, or the given statements of those funds - or the given
 * performance decks. The server answers as soon as the funds are queued;
 * the run then shows them through to Generated, Failed or Blocked. A
 * refusal (already being generated, no fund in the run) comes back as
 * `error`, the server's own sentence. `accountDocuments` is how many decks
 * the generation also makes.
 */
export const generateRunStatements = async (
  runId: string,
  target: GenerateTarget = {},
  accessToken: string | null = null
): Promise<{ count: number | null; accountDocuments?: number; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INVESTOR_STATEMENTS.RUN_GENERATE, { id: runId }, {})
    const data: Record<string, string[]> = {}
    if (target.runFundIds?.length) data.run_fund_ids = target.runFundIds
    if (target.statementIds?.length) data.statement_ids = target.statementIds
    if (target.accountDocumentIds?.length) data.account_document_ids = target.accountDocumentIds
    // the document groups to generate (reports, statements); every group when absent
    if (target.documentGroupCodes?.length) data.document_group_codes = target.documentGroupCodes
    const response = await fetch(url, {
      method: API_ROUTES.INVESTOR_STATEMENTS.RUN_GENERATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data })
    })
    if (!response.ok) return { count: null, error: await failureText(response) }
    const json = (await response.json().catch(() => ({}))) as {
      data?: { count?: number; account_documents?: number } | null
    }
    return { count: json.data?.count ?? 0, accountDocuments: json.data?.account_documents ?? 0 }
  } catch (error) {
    return { count: null, error: unexpected(error) }
  }
}

/** Delete one investor performance deck of a run; its stored file goes to
 *  the trash. A refusal (being generated; in a reports email on its way;
 *  issued and not an administrator) comes back as `error`, the server's
 *  own sentence. */
export const deleteAccountDocument = async (
  accountDocumentId: string,
  accessToken: string | null = null
): Promise<{ success: boolean; filesNotRemoved: number; error?: string }> => {
  try {
    const url = buildFullUrl(
      BACKEND_BASE_URL,
      API_ROUTES.INVESTOR_STATEMENTS.ACCOUNT_DOCUMENT_DELETE,
      { id: accountDocumentId },
      {}
    )
    const response = await fetch(url, {
      method: API_ROUTES.INVESTOR_STATEMENTS.ACCOUNT_DOCUMENT_DELETE.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) return { success: false, filesNotRemoved: 0, error: await failureText(response) }
    const json = (await response.json().catch(() => ({}))) as { data?: { files_not_removed?: number } }
    return { success: true, filesNotRemoved: json.data?.files_not_removed ?? 0 }
  } catch (error) {
    return { success: false, filesNotRemoved: 0, error: unexpected(error) }
  }
}

/** Delete a run; its stored statements go to the trash. A refusal (being
 *  generated; issued statements and not an administrator) comes back as
 *  `error`, the server's own sentence. */
export const deleteInvestorStatementRun = async (
  runId: string,
  accessToken: string | null = null
): Promise<{ success: boolean; filesNotRemoved: number; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INVESTOR_STATEMENTS.RUN_DELETE, { id: runId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.INVESTOR_STATEMENTS.RUN_DELETE.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) return { success: false, filesNotRemoved: 0, error: await failureText(response) }
    const json = (await response.json().catch(() => ({}))) as { data?: { files_not_removed?: number } }
    return { success: true, filesNotRemoved: json.data?.files_not_removed ?? 0 }
  } catch (error) {
    return { success: false, filesNotRemoved: 0, error: unexpected(error) }
  }
}

const postRunFundAction = async (
  route: RouteDefinition,
  runFundId: string,
  accessToken: string | null,
  documentGroupCodes?: string[] | null
): Promise<InvestorStatementRunResponse> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, route, { id: runFundId }, {}), {
      method: route.method,
      headers: authHeaders(accessToken),
      // the document groups to act on (reports, statements); every group when absent
      body: JSON.stringify({ data: documentGroupCodes?.length ? { document_group_codes: documentGroupCodes } : {} })
    })
    if (!response.ok) return { data: null, error: await failureText(response) }
    const json = (await response.json()) as { data?: unknown }
    return { data: validateInvestorStatementRun(json.data) }
  } catch (error) {
    return { data: null, error: unexpected(error) }
  }
}

/** Mark the documents of a fund of a run as issued - every document group,
 *  or the groups named. Answers with the run. */
export const issueRunFund = (
  runFundId: string,
  accessToken: string | null = null,
  documentGroupCodes: string[] | null = null
) => postRunFundAction(API_ROUTES.INVESTOR_STATEMENTS.RUN_FUND_ISSUE, runFundId, accessToken, documentGroupCodes)

/** Undo Mark as issued (an application administrator) - every document
 *  group, or the groups named. Answers with the run. */
export const unissueRunFund = (
  runFundId: string,
  accessToken: string | null = null,
  documentGroupCodes: string[] | null = null
) => postRunFundAction(API_ROUTES.INVESTOR_STATEMENTS.RUN_FUND_UNISSUE, runFundId, accessToken, documentGroupCodes)

/** The Review workbooks of a run as one zip - one xlsx per legal fund as
 *  of the run's period end, built live - or of one fund of the run (a
 *  consolidated fund: one per legal entity). Saved under the server's
 *  filename. */
export const downloadRunReviewWorkbooks = async (
  runId: string,
  runFundId: string | null,
  accessToken: string | null = null
): Promise<{ success: boolean; error?: string }> => {
  try {
    const url = buildFullUrl(
      getBackendBaseUrl(),
      API_ROUTES.INVESTOR_STATEMENTS.RUN_REVIEW_WORKBOOKS,
      { id: runId },
      runFundId ? { run_fund_id: runFundId } : {}
    )
    const response = await fetch(url, {
      method: API_ROUTES.INVESTOR_STATEMENTS.RUN_REVIEW_WORKBOOKS.method,
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
    })
    if (!response.ok) {
      if (response.status === 401) return { success: false, error: 'Authentication failed - please log in again' }
      const json = (await response.json().catch(() => ({}))) as { message?: string; error?: string }
      return { success: false, error: json.message || json.error || `Failed to download the review workbooks: ${response.status}` }
    }
    const blob = await response.blob()
    saveBlob(blob, filenameFromContentDisposition(response.headers.get('Content-Disposition'), 'review-workbooks.zip'))
    return { success: true }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error downloading the review workbooks:', error)
    return { success: false, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

/** "Out of date" for one fund of a run: its stored statements against the
 *  books and settings as they are now. Takes a second or two. */
export const fetchRunFundChanges = async (
  runFundId: string,
  accessToken: string | null = null
): Promise<{ data: RunFundChanges | null; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INVESTOR_STATEMENTS.RUN_FUND_CHANGES, { id: runFundId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.INVESTOR_STATEMENTS.RUN_FUND_CHANGES.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) return { data: null, error: await failureText(response) }
    const json = (await response.json()) as { data?: unknown }
    return { data: validateRunFundChanges(json.data) }
  } catch (error) {
    return { data: null, error: unexpected(error) }
  }
}

/** The agent-written company short descriptions the run's stored reports
 *  printed that still need a person, per document id. */
export const fetchRunDescriptionWarnings = async (
  runId: string,
  accessToken: string | null = null
): Promise<{ data: RunDescriptionWarnings | null; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INVESTOR_STATEMENTS.RUN_DESCRIPTION_WARNINGS, { id: runId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.INVESTOR_STATEMENTS.RUN_DESCRIPTION_WARNINGS.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) return { data: null, error: await failureText(response) }
    const json = (await response.json()) as { data?: unknown }
    return { data: validateRunDescriptionWarnings(json.data) }
  } catch (error) {
    return { data: null, error: unexpected(error) }
  }
}

/** The statement runs a fund, or a consolidated fund, is in. */
export const fetchFundStatementRuns = async (
  owner: { fundId?: string | null; fundGroupId?: string | null },
  accessToken: string | null = null
): Promise<{ data: FundStatementRuns | null; error?: string }> => {
  try {
    const route = owner.fundGroupId
      ? API_ROUTES.INVESTOR_STATEMENTS.FUND_GROUP_RUNS
      : API_ROUTES.INVESTOR_STATEMENTS.FUND_RUNS
    const url = buildFullUrl(BACKEND_BASE_URL, route, { id: owner.fundGroupId || owner.fundId || '' }, {})
    const response = await fetch(url, { method: route.method, headers: authHeaders(accessToken) })
    if (!response.ok) return { data: null, error: await failureText(response) }
    const json = (await response.json()) as { data?: unknown }
    return { data: validateFundStatementRuns(json.data) }
  } catch (error) {
    return { data: null, error: unexpected(error) }
  }
}

// ── Sending (stage 6) ────────────────────────────────────────────────────────

/** What "Send statements" would create for a run: every fund with whether
 *  it can be sent, per account its attachments, size, recipients. With
 *  `runFundIds` only those funds; `onlyUnsent` leaves out statements already
 *  sent. */
export const fetchStatementSendPlan = async (
  runId: string,
  options: { runFundIds?: string[] | null; onlyUnsent?: boolean; documentGroupCodes?: string[] | null } = {},
  accessToken: string | null = null
): Promise<{ data: InvestorStatementSendPlan | null; error?: string }> => {
  try {
    const query: Record<string, string | boolean> = {}
    if (options.runFundIds) query.run_fund_ids = options.runFundIds.join(',')
    if (options.onlyUnsent) query.only_unsent = true
    // the document groups to send (reports, statements); every group when absent
    if (options.documentGroupCodes?.length) query.document_group_codes = options.documentGroupCodes.join(',')
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INVESTOR_STATEMENTS.SEND_PLAN, { id: runId }, query)
    const response = await fetch(url, {
      method: API_ROUTES.INVESTOR_STATEMENTS.SEND_PLAN.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) return { data: null, error: await failureText(response) }
    const json = (await response.json()) as { data?: unknown }
    if (!json.data) return { data: null, error: 'The server returned no plan' }
    return { data: validateInvestorStatementSendPlan(json.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching the statement email plan:', error)
    return { data: null, error: unexpected(error) }
  }
}

/** One draft of a set: the part it is of the set. */
export interface CreatedStatementCampaignPart {
  email_campaigns__id: string
  email_campaigns__name: string
  part_no: number
  /** This part could not be filled (see prog__sync_error on the set). */
  prog__sync_error?: string | null
}

export interface CreatedStatementCampaign {
  /** The first draft of the set - the one that opens. */
  email_campaigns__id: string
  email_campaigns__name: string
  /** The document groups the email was created for (reports, statements). */
  document_group_codes: string[]
  /** How many drafts the set has: an account whose documents do not fit one
   *  email is sent in several (docs/QUARTERLY_REPORT_DISTRIBUTION_DESIGN.md
   *  d.3); 1 for most sends. */
  part_count: number
  /** Every draft of the set, part 1 first. */
  campaigns: CreatedStatementCampaignPart[]
  /** A draft exists but could not be filled: the queue refuses it until
   *  "Refresh from statement run" has run. */
  prog__sync_error?: string | null
}

/**
 * Create the draft statement email(s) of a run for the chosen funds and
 * document groups (every group when none are named): one draft, or one per
 * part when an account's documents do not fit one email. A refusal (a fund
 * not issued, nobody to send to) comes back as `error`, the server's own
 * sentence.
 */
export const createStatementCampaign = async (
  runId: string,
  runFundIds: string[],
  onlyUnsent = false,
  accessToken: string | null = null,
  documentGroupCodes: string[] | null = null
): Promise<{ data: CreatedStatementCampaign | null; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INVESTOR_STATEMENTS.CAMPAIGN_CREATE, { id: runId }, {})
    const data: Record<string, unknown> = { run_fund_ids: runFundIds, only_unsent: onlyUnsent }
    if (documentGroupCodes?.length) data.document_group_codes = documentGroupCodes
    const response = await fetch(url, {
      method: API_ROUTES.INVESTOR_STATEMENTS.CAMPAIGN_CREATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data })
    })
    if (!response.ok) return { data: null, error: await failureText(response) }
    const json = (await response.json()) as { data?: Partial<CreatedStatementCampaign> | null }
    const id = json.data?.email_campaigns__id
    if (!id) return { data: null, error: 'The server did not return the email it created' }
    const name = json.data?.email_campaigns__name || 'Investor statements'
    const campaigns: CreatedStatementCampaignPart[] = (json.data?.campaigns ?? [])
      .filter((c) => !!c?.email_campaigns__id)
      .map((c, i) => ({
        email_campaigns__id: c.email_campaigns__id,
        email_campaigns__name: c.email_campaigns__name || name,
        part_no: c.part_no ?? i + 1,
        prog__sync_error: c.prog__sync_error ?? null
      }))
    return {
      data: {
        email_campaigns__id: id,
        email_campaigns__name: name,
        document_group_codes: json.data?.document_group_codes ?? documentGroupCodes ?? [],
        part_count: json.data?.part_count ?? Math.max(campaigns.length, 1),
        campaigns: campaigns.length > 0 ? campaigns : [{ email_campaigns__id: id, email_campaigns__name: name, part_no: 1 }],
        prog__sync_error: json.data?.prog__sync_error ?? null
      }
    }
  } catch (error) {
    return { data: null, error: unexpected(error) }
  }
}

/**
 * "Refresh from statement run" on a draft statement email: its recipients
 * and per-account attachments are rebuilt from the run as it is now - for
 * every draft of its set (the parts of one Create emails). `partCount` is
 * the set's size, `refreshed` how many drafts were rebuilt and `skipped`
 * the names of siblings left as they are (no longer drafts).
 */
export const refreshStatementCampaign = async (
  runId: string,
  emailCampaignId: string,
  accessToken: string | null = null
): Promise<{
  success: boolean
  recipientCount?: number
  partCount?: number
  refreshed?: number
  skipped?: string[]
  error?: string
}> => {
  try {
    const url = buildFullUrl(
      BACKEND_BASE_URL,
      API_ROUTES.INVESTOR_STATEMENTS.CAMPAIGN_REFRESH,
      { id: runId, campaignId: emailCampaignId },
      {}
    )
    const response = await fetch(url, {
      method: API_ROUTES.INVESTOR_STATEMENTS.CAMPAIGN_REFRESH.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) return { success: false, error: await failureText(response) }
    const json = (await response.json().catch(() => ({}))) as {
      data?: { recipient_count?: number; part_count?: number; campaigns?: unknown[]; skipped?: string[] } | null
    }
    return {
      success: true,
      recipientCount: json.data?.recipient_count ?? 0,
      partCount: json.data?.part_count ?? 1,
      refreshed: json.data?.campaigns?.length ?? 1,
      skipped: json.data?.skipped ?? []
    }
  } catch (error) {
    return { success: false, error: unexpected(error) }
  }
}
